import {Component, OnDestroy, OnInit} from '@angular/core';
import {AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators} from '@angular/forms';
import {
    CampaignDto,
    ConfiguratorDatastoreService,
    ConfiguratorEtlService,
    ConfiguratorI18nCacheService,
    ConfiguratorProviderService, ConfiguratorScenarioService, DetailCampaignDTO,
    EngineCampaignService, EtlConf, ICampaignAssociationItem, IPersonalCampaignAssociation,
    MarketingScenarioProcess,
    ProviderDTO, SaveQueryExtractConf, ScenarioDTO
} from '@tj/tj-dmp-lib-api';
import {ActivatedRoute, NavigationExtras, Router} from '@angular/router';
import {forkJoin, Subscription} from 'rxjs';
import {ConfirmationService, LazyLoadEvent, MessageService, SelectItem} from 'primeng/api';
import * as moment from 'moment';
import {Paginator} from "../../../../components/paginator";
import { TranslateService } from '@ngx-translate/core';
import {AccountService} from '../../../../auth/account.service';

export interface AlertEmailToken {
    text: string;
    valid: boolean;
}

@Component({
    selector: 'dmp-campaign-edit',
    templateUrl: './campaign-edit.component.html',
    styleUrls: ['./campaign-edit.component.scss'],
    providers: [ConfirmationService],
    standalone: false
})
export class CampaignEditComponent implements OnInit, OnDestroy {
    private langChangeSub?: Subscription;
    private pageMode: 'create' | 'edit' | 'detail' = 'detail';

    breadcrumb: {label: string; routerLink?: string[]}[] = [];
    description: string;
    submitted = false;
    form: UntypedFormGroup;
    actual: DetailCampaignDTO;
    listFlux: SelectItem[] = [];
    listType: SelectItem[] = [];
    listProvider: SelectItem[] = [];
    listProduct: SelectItem[] = [];
    listSupport: SelectItem[] = [];
    listSchedule: SelectItem[] = [];
    listDataType: SelectItem[] = [];
    listStatus: SelectItem[] = [];
    listScenario: SelectItem[] = [];
    listImport: SelectItem[] = [];
    listExport: SelectItem[] = [];
    private allImportEtls: EtlConf[] | null = null;
    private allExportEtls: EtlConf[] | null = null;
    /** Map ETL id -> name for displaying export origin in historique */
    exportEtlNameById: Map<string, string> = new Map();
    private allScenariosByPartner = new Map<string, ScenarioDTO[]>();
    listCampaignMode: SelectItem[] = [];
    tenant: string;

    disabled = false;

    campaignItems: Array<ICampaignAssociationItem>;
    campaignItem: ICampaignAssociationItem;
    
    /**
     * Translates support enum value or ETL ID to display label for historique.
     * - Support enums (FILE, API, etc.) -> French label
     * - ETL ID (MongoDB ObjectId, 24 hex chars) -> ETL name from configurator
     * - Scenario name -> as-is
     */
    translateSupport(origin: string): string {
        if (!origin) {
            return this.translate.instant('campaign.edit.unknown');
        }
        const supportKey = `campaign.support_${origin}`;
        const translated = this.translate.instant(supportKey);
        if (translated !== supportKey) {
            return translated;
        }
        // If it looks like an ETL ID (MongoDB ObjectId: 24 hex chars), resolve to name
        if (/^[a-f0-9]{24}$/i.test(origin)) {
            return this.exportEtlNameById.get(origin) || origin;
        }
        // Scenario name or other -> return as-is
        return origin;
    }

    /**
     * Translates contact status enum value to French label
     */
    translateContactStatus(status: string): string {
        if (!status) {
            return '';
        }
        const key = `campaign.associationStatus.${status}`;
        const translated = this.translate.instant(key);
        return translated !== key ? translated : status;
    }
    campaignJoins: Array<IPersonalCampaignAssociation>;
    itemPaginator = new Paginator();
    personalPaginator = new Paginator();
    statusEnum = CampaignDto.StatusEnum;
    dataTypeEnum = CampaignDto.DataTypeEnum;
    activeAccordionIndexes: number[] = [0]; // Open first section by default
    readonly accordionTabCount = 5;

    /** Destinataires des notifications campagne (affichage chips). */
    alertEmailTokens: AlertEmailToken[] = [];
    alertEmailDraft = '';

    expandAllAccordion(): void {
        this.activeAccordionIndexes = Array.from({ length: this.accordionTabCount }, (_, i) => i);
    }

    collapseAllAccordion(): void {
        this.activeAccordionIndexes = [];
    }

    constructor(
        private readonly fb: UntypedFormBuilder,
        private readonly engineCampaignService: EngineCampaignService,
        private readonly route: ActivatedRoute,
        private readonly datastoreResourceService: ConfiguratorDatastoreService,
        private readonly i18nCacheService: ConfiguratorI18nCacheService,
        private readonly messageService: MessageService,
        private readonly router: Router,
        private readonly providerService: ConfiguratorProviderService,
        private readonly translate: TranslateService,
        private readonly configuratorScenarioService: ConfiguratorScenarioService,
        private readonly configuratorEtlService: ConfiguratorEtlService,
        private readonly confirmationService: ConfirmationService,
        private readonly accountService: AccountService
    ) {
    }

    ngOnInit(): void {
        const isNew = this.router.url.includes('new');
        const isEdit = this.router.url.includes('edit');
        this.pageMode = isNew ? 'create' : (isEdit ? 'edit' : 'detail');

        this.rebuildPageLabels();
        this.langChangeSub = this.translate.onLangChange.subscribe(() => {
            this.rebuildPageLabels();
            this.initList();
            if (this.form?.get('partner')?.value) {
                this.loadScenarios();
            }
        });

        if (this.pageMode === 'detail') {
            this.disabled = true;
        }
        this.tenant = this.route.snapshot.paramMap.get('tenant');

        this.form = this.fb.group({
            id: [],
            partner: ['', [Validators.required]],
            code: [{value: '', disabled: this.disabled}, [Validators.required, Validators.maxLength(10)]],
            name: [{value: '', disabled: this.disabled}, [Validators.required, Validators.maxLength(255)]],
            description: [{value: '', disabled: this.disabled}, Validators.maxLength(255)],
            destination: [{value: '', disabled: this.disabled}, Validators.maxLength(10)],
            flux: [CampaignDto.FluxEnum.IMPORT, Validators.required],
            product: ['', Validators.required],
            provider: ['', [Validators.maxLength(10)]],
            schedule: [CampaignDto.ScheduleEnum.DAILY, Validators.required],
            source: [{value: '', disabled: this.disabled}, Validators.maxLength(10)],
            support: [CampaignDto.SupportEnum.API, Validators.required],
            type: [CampaignDto.TypeEnum.AFFILIATE, Validators.required],
            tenant: [this.tenant],
            dateStart: [new Date(), Validators.required],
            dateEnd: [''],
            costInitial: [0, [Validators.required, Validators.min(0)]],
            costUnitContact: [null, [Validators.min(0)]],
            costUnitOrder: [null, [Validators.min(0)]],
            disable: [false],
            // À supprimer: transferMode lors de la suppression totale
            // transferMode: [{ value: '', disabled: this.disabled }],
            scenario: [''],
            importEtl: [''],
            exportEtl: [''],
            dataType: [CampaignDto.DataTypeEnum.ORDERS, Validators.required],
            campaignMode: ['SPREAD_EXPENSE', Validators.required],
            objectiveUnits: [null],
            objectiveBudgetMax: [null, [Validators.min(0)]],
            financialNote: ['', [Validators.maxLength(4000)]],
            alertThresholdUnits: [null],
            alertThresholdCost: [null],
            alertEmailRecipients: [''],
            utmSource: [{ value: '', disabled: this.disabled }, Validators.maxLength(50)],
            utmMedium: [{ value: '', disabled: this.disabled }, Validators.maxLength(50)],
            utmCampaign: [{ value: '', disabled: this.disabled }, Validators.maxLength(50)],
            utmTerm: [{ value: '', disabled: this.disabled }, Validators.maxLength(50)],
            utmContent: [{ value: '', disabled: this.disabled }, Validators.maxLength(50)],
            status: [CampaignDto.StatusEnum.DRAFT]
        }, { validators: [this.atLeastOneUtmValidator, this.campaignModeValidator] });

        // Read mode must lock all controls at form level (some custom inputs rely on control disabled state).
        if (this.disabled) {
            this.form.disable({ emitEvent: false });
        }

        this.actual = null;
        // init list
        this.listProvider = [];
        this.listDataType = [];
        this.listStatus = [];
        this.listScenario = [];
        this.initList();
        forkJoin({
            providers: this.providerService.getAllProvidersUsingGET(this.tenant),
            exportEtls: this.configuratorEtlService.getAllEtls(this.tenant, ['EXPORT'])
        }).subscribe(({providers, exportEtls}) => {
            this.listProvider = (providers || []).map(p => ({
                label: p.name,
                value: (p.code || '').trim()
            }));
            this.allExportEtls = exportEtls || [];
            (this.allExportEtls || []).forEach(e => this.exportEtlNameById.set(e.id, e.name || e.code || e.id));
            this.initOnUpdate();
        });

        this.form.get('partner').valueChanges.subscribe(() => this.loadScenarios());
        this.form.get('product').valueChanges.subscribe(() => this.loadScenarios());
        this.form.get('dataType').valueChanges.subscribe(() => this.loadScenarios());
        this.form.get('flux').valueChanges.subscribe((flux: string) => {
            this.updateProviderValidator(flux);
            this.updateScenarioExportValidators(flux);
            this.loadScenarios();
            this.loadImports();
            this.loadExports();
        });
        this.form.get('partner').valueChanges.subscribe(() => this.loadImports());
        this.form.get('partner').valueChanges.subscribe(() => this.loadExports());
        this.form.get('product').valueChanges.subscribe(() => this.loadExports());
        this.form.get('dataType').valueChanges.subscribe(() => {
            this.updateListCampaignMode();
            this.loadExports();
            this.updateCampaignModeValidators();
            this.resetHiddenCampaignModeFields();
        });
        this.form.get('campaignMode').valueChanges.subscribe(() => {
            this.updateCampaignModeValidators();
            this.resetHiddenCampaignModeFields();
            this.rebuildAlertEmailTokensFromForm();
        });
        this.loadScenarios();
        this.loadImports();
        this.loadExports();
        this.updateProviderValidator(this.form.get('flux')?.value);
        this.updateScenarioExportValidators(this.form.get('flux')?.value);
        this.updateCampaignModeValidators();
        this.resetHiddenCampaignModeFields();
    }

    ngOnDestroy(): void {
        this.langChangeSub?.unsubscribe();
    }

    private rebuildPageLabels(): void {
        const pageKey = this.pageMode === 'create' ? 'Create'
            : (this.pageMode === 'edit' ? 'Edit' : 'Detail');
        this.breadcrumb = [
            {label: this.translate.instant('menu.marketing.label')},
            {label: this.translate.instant('menu.marketing.campaigns'), routerLink: ['/h/marketing/campaign']},
            {label: this.translate.instant(`campaign.edit.page.breadcrumb${pageKey}`)}
        ];
        this.description = this.translate.instant(`campaign.edit.page.description${pageKey}`);
    }

    private updateScenarioExportValidators(flux: string): void {
        const scenarioCtrl = this.form.get('scenario');
        const exportEtlCtrl = this.form.get('exportEtl');
        if (flux === 'EXPORT_SCENARIO') {
            scenarioCtrl?.setValidators(Validators.required);
            exportEtlCtrl?.setValidators([]);
        } else if (flux === 'EXPORT_DIRECT') {
            scenarioCtrl?.setValidators([]);
            exportEtlCtrl?.setValidators(Validators.required);
        } else {
            scenarioCtrl?.setValidators([]);
            exportEtlCtrl?.setValidators([]);
        }
        scenarioCtrl?.updateValueAndValidity();
        exportEtlCtrl?.updateValueAndValidity();
    }

    private updateProviderValidator(flux: string): void {
        const providerCtrl = this.form.get('provider');
        if (flux === 'IMPORT') {
            providerCtrl?.setValidators([Validators.required, Validators.maxLength(10)]);
        } else {
            providerCtrl?.setValidators([Validators.maxLength(10)]);
        }
        providerCtrl?.updateValueAndValidity();
    }

    onProviderChange(value: string): void {
        const normalized = (value || '').trim();
        this.form.get('provider')?.setValue(normalized, { emitEvent: false });
    }

    onDisabledChange(){
        // Vérifier si la campagne peut être modifiée (uniquement Brouillon ou Suspendue)
        if (this.actual?.campaignDto) {
            const status = this.actual.campaignDto.status;
            if (status !== CampaignDto.StatusEnum.DRAFT && status !== CampaignDto.StatusEnum.SUSPENDED) {
                this.messageService.add({
                    key: 'component',
                    severity: 'error',
                    summary: this.translate.instant('campaign.edit.message.editForbiddenSummary'),
                    detail: this.translate.instant('campaign.edit.message.editForbiddenDetail')
                });
                return;
            }
        }

        this.disabled = false;
        this.pageMode = 'edit';
        this.rebuildPageLabels();

        // Liste des champs modifiables pour toutes les campagnes
        ['code', 'name', 'description', 'destination','source', /* transferMode */ 'utmSource', 'utmMedium', 'utmCampaign', 'utmTerm', 'utmContent',
            'campaignMode', 'objectiveUnits', 'objectiveBudgetMax', 'costInitial', 'costUnitContact', 'costUnitOrder', 'alertThresholdUnits', 'alertThresholdCost', 'alertEmailRecipients'].forEach(field => {
            this.form.get(field).enable();
        });

        // Si la campagne n'est pas en mode Brouillon, appliquer les restrictions
        if (this.actual?.campaignDto?.status !== CampaignDto.StatusEnum.DRAFT) {
            this.applyNonDraftFieldRestrictions();
        }
    }

    private applyNonDraftFieldRestrictions() {
        // Lorsque la campagne n'est pas en mode Brouillon, les champs structurants/coût deviennent en lecture seule.
        [
            'partner', 'code', 'product', 'flux', 'dataType',
            'provider', 'importEtl', 'exportEtl', 'scenario',
            'dateStart', 'campaignMode',
            'costInitial', 'costUnitContact', 'costUnitOrder',
            'utmSource', 'utmMedium', 'utmCampaign', 'utmTerm', 'utmContent'
        ].forEach(field => {
            this.form.get(field)?.disable();
        });
    }

    private initList() {
        const defaultItem: SelectItem = {label: this.translate.instant('campaign.edit.placeholder.select'), value: null};
        this.listType.push(...[
            {label: this.translate.instant('campaign.type_AFFILIATE'), value: CampaignDto.TypeEnum.AFFILIATE},
            {label: this.translate.instant('campaign.type_NETWORK'), value: CampaignDto.TypeEnum.NETWORK},
            {label: this.translate.instant('campaign.type_MEDIA'), value: CampaignDto.TypeEnum.MEDIA}]);
        this.listFlux.push(...[
            {label: this.translate.instant('campaign.flux_IMPORT'), value: 'IMPORT'},
            {label: this.translate.instant('campaign.flux_EXPORT_SCENARIO'), value: 'EXPORT_SCENARIO'},
            {label: this.translate.instant('campaign.flux_EXPORT_DIRECT'), value: 'EXPORT_DIRECT'}]);
        this.listProduct.push(...[
            defaultItem,
            {label: this.translate.instant('campaign.product_FLIGHT'), value: 'FLIGHT'},
            {label: this.translate.instant('campaign.product_HOTEL'), value: 'HOTEL'},
            {label: this.translate.instant('campaign.product_CAR'), value: 'CAR'},
            {label: this.translate.instant('campaign.product_PARKING'), value: 'PARKING'},
            {label: this.translate.instant('campaign.product_ALL'), value: 'ALL'}]);
        this.listSupport.push(...[
            {label: this.translate.instant('campaign.support_API'), value: CampaignDto.SupportEnum.API},
            {label: this.translate.instant('campaign.support_SMS'), value: CampaignDto.SupportEnum.SMS},
            {label: this.translate.instant('campaign.support_FILE'), value: CampaignDto.SupportEnum.FILE},
            {label: this.translate.instant('campaign.support_MAIL'), value: CampaignDto.SupportEnum.MAIL},
            {label: this.translate.instant('campaign.support_FORM'), value: CampaignDto.SupportEnum.FORM},
            {label: this.translate.instant('campaign.support_EMAILING'), value: CampaignDto.SupportEnum.EMAILING}]);
        this.listSchedule.push(...[
            {label: this.translate.instant('campaign.schedule_MANUAL'), value: CampaignDto.ScheduleEnum.MANUAL},
            {label: this.translate.instant('campaign.schedule_DAILY'), value: CampaignDto.ScheduleEnum.DAILY},
            {label: this.translate.instant('campaign.schedule_HOURLY'), value: CampaignDto.ScheduleEnum.HOURLY},
            {label: this.translate.instant('campaign.schedule_REAL_TIME'), value: CampaignDto.ScheduleEnum.REALTIME}]);
        this.listDataType.push(...[
            {label: this.translate.instant('campaign.dataType.ORDERS'), value: CampaignDto.DataTypeEnum.ORDERS},
            {label: this.translate.instant('campaign.dataType.CONTACTS'), value: CampaignDto.DataTypeEnum.CONTACTS}
        ]);
        this.updateListCampaignMode();
        this.listStatus.push(...[
            {label: this.translate.instant('campaign.status.DRAFT'), value: CampaignDto.StatusEnum.DRAFT},
            {label: this.translate.instant('campaign.status.IN_PROGRESS'), value: CampaignDto.StatusEnum.INPROGRESS},
            {label: this.translate.instant('campaign.status.SUSPENDED'), value: CampaignDto.StatusEnum.SUSPENDED},
            {label: this.translate.instant('campaign.status.TERMINATED'), value: CampaignDto.StatusEnum.TERMINATED},
            {label: this.translate.instant('campaign.status.ARCHIVED'), value: CampaignDto.StatusEnum.ARCHIVED},
            {label: this.translate.instant('campaign.status.DELETED'), value: CampaignDto.StatusEnum.DELETED},
        ]);
    }

    getStatusLabel(status: CampaignDto.StatusEnum | string | undefined): string {
        if (!status) {
            return this.translate.instant('campaign.status.DRAFT');
        }
        return this.translate.instant(`campaign.status.${status}`);
    }

    t(key: string, params?: Record<string, unknown>): string {
        return this.translate.instant(key, params);
    }

    private financialSuffix(): 'Cost' | 'Revenue' {
        return CampaignEditComponent.isExportFlux(this.currentFlux()) ? 'Revenue' : 'Cost';
    }

    /** campaign.edit.{section}.{key}Cost|Revenue */
    fin(section: 'field' | 'hint' | 'tracking', key: string): string {
        return this.translate.instant(`campaign.edit.${section}.${key}${this.financialSuffix()}`);
    }

    static isExportFlux(flux?: string | CampaignDto.FluxEnum): boolean {
        return flux === CampaignDto.FluxEnum.EXPORTSCENARIO || flux === CampaignDto.FluxEnum.EXPORTDIRECT;
    }

    currentFlux(): string | undefined {
        return this.form?.get('flux')?.value ?? this.actual?.campaignDto?.flux;
    }

    getCostTrackingTitle(): string {
        const status = this.actual?.campaignDto?.status;
        const mode = this.actual?.campaignDto?.campaignMode;
        if (mode === 'SPREAD_EXPENSE' && status !== CampaignDto.StatusEnum.TERMINATED) {
            return this.fin('tracking', 'forecastTitle'); // Prévision des coûts
        }
        if (status === CampaignDto.StatusEnum.INPROGRESS) {
            return this.fin('tracking', 'inProgressTitle'); // Prévision des coûts
        }
        return this.fin('tracking', 'title'); // Suivi des coûts
    }

    getCostTrackingNotes(): string[] {
        const notes: string[] = [];
        const status = this.actual?.campaignDto?.status;
        const mode = this.actual?.campaignDto?.campaignMode;
        if (mode === 'SPREAD_EXPENSE' && status !== CampaignDto.StatusEnum.TERMINATED) {
            notes.push(this.fin('tracking', 'forecastNote'));
        }
        return notes;
    }

    getCostTrackingBottomNotes(): string[] {
        const notes: string[] = [];
        const status = this.actual?.campaignDto?.status;
        const mode = this.actual?.campaignDto?.campaignMode;
        const flux = this.actual?.campaignDto?.flux;
        if (mode === 'SPREAD_EXPENSE') {
            if (status === CampaignDto.StatusEnum.TERMINATED) {
                notes.push(this.translate.instant('campaign.edit.tracking.spreadTerminated'));
            } else {
                notes.push(this.fin('tracking', 'spreadEstimate'));
            }
        }
        if (status === CampaignDto.StatusEnum.TERMINATED && flux === CampaignDto.FluxEnum.IMPORT) {
            if (mode === 'SPREAD_EXPENSE') {
                notes.push(this.translate.instant('campaign.edit.tracking.terminatedImportSpread'));
            } else {
                notes.push(this.translate.instant('campaign.edit.tracking.terminatedImport'));
            }
        }
        if (status === CampaignDto.StatusEnum.TERMINATED && CampaignEditComponent.isExportFlux(flux)) {
            if (mode === 'SPREAD_EXPENSE') {
                notes.push(this.translate.instant('campaign.edit.tracking.terminatedExportSpread'));
            }
        }
        return notes;
    }

    getAssociationOrderCount(): number | null {
        if (this.actual?.campaignDto?.dataType !== CampaignDto.DataTypeEnum.ORDERS) {
            return null;
        }
        return this.actual?.appliedUnits ?? 0;
    }

    getAssociationContactCount(): number | null {
        if (this.actual?.campaignDto?.dataType === CampaignDto.DataTypeEnum.CONTACTS) {
            return this.actual?.appliedUnits ?? 0;
        }
        return null;
    }

    getAssociationNewContactCount(): number | null {
        const dto: any = this.actual;
        if (this.actual?.campaignDto?.dataType !== CampaignDto.DataTypeEnum.ORDERS) {
            return null;
        }
        const newContacts = dto?.newContactsApplied;
        return newContacts != null ? Number(newContacts) : null;
    }

    getDefinedObjectiveBudgetMax(): number | null {
        const max = this.actual?.campaignDto?.objectiveBudgetMax;
        return max != null ? Number(max) : null;
    }

    isEstimatedTotalBudget(): boolean {
        const c = this.actual?.campaignDto;
        return c?.campaignMode === 'UNITS_MAX'
            && c.objectiveBudgetMax == null
            && c.objectiveUnits != null;
    }

    getEstimatedTotalBudget(): number | null {
        if (!this.isEstimatedTotalBudget()) {
            return null;
        }
        const c = this.actual?.campaignDto;
        if (!c || c.objectiveUnits == null) {
            return null;
        }
        const units = Number(c.objectiveUnits);
        const initial = Number(c.costInitial ?? 0);
        const contact = Number(c.costUnitContact ?? 0) * units;
        const order = c.dataType === CampaignDto.DataTypeEnum.ORDERS
            ? Number(c.costUnitOrder ?? 0) * units
            : 0;
        const unitTotal = contact + order;
        if (CampaignEditComponent.isExportFlux(c.flux)) {
            return unitTotal - initial;
        }
        return initial + unitTotal;
    }

    getDefinedSpreadPool(): number | null {
        const c = this.actual?.campaignDto;
        if (!c || c.campaignMode !== 'SPREAD_EXPENSE' || c.objectiveBudgetMax == null) {
            return null;
        }
        const pool = Number(c.objectiveBudgetMax) - Number(c.costInitial ?? 0);
        return pool > 0 ? pool : null;
    }

    getDefinedContactUnitCost(): number | null {
        const c = this.actual?.campaignDto;
        if (!c || c.campaignMode === 'SPREAD_EXPENSE') {
            return null;
        }
        const value = c.costUnitContact;
        return value != null && Number(value) > 0 ? Number(value) : null;
    }

    getDefinedOrderUnitCost(): number | null {
        const c = this.actual?.campaignDto;
        if (!c || c.dataType !== CampaignDto.DataTypeEnum.ORDERS || c.campaignMode === 'SPREAD_EXPENSE') {
            return null;
        }
        const value = c.costUnitOrder;
        return value != null ? Number(value) : null;
    }

    getBudgetConsumed(): number | null {
        if (this.actual?.incrementalBudget == null) {
            return null;
        }
        const incremental = Number(this.actual.incrementalBudget ?? 0);
        const initial = Number(this.actual.campaignDto?.costInitial ?? 0);
        const mode = this.actual.campaignDto?.campaignMode;
        const isExport = CampaignEditComponent.isExportFlux(this.actual.campaignDto?.flux);
        if (mode === 'SPREAD_EXPENSE' && isExport) {
            return incremental;
        }
        if (isExport) {
            return incremental - initial;
        }
        return incremental + initial;
    }

    getObservedTotalLabel(): string {
        const mode = this.actual?.campaignDto?.campaignMode;
        const isExport = CampaignEditComponent.isExportFlux(this.actual?.campaignDto?.flux);
        if (mode === 'SPREAD_EXPENSE' && isExport) {
            return this.translate.instant('campaign.edit.field.spreadObservedRevenue');
        }
        return this.fin('field', 'observedTotal');
    }

    getBudgetConsumedRatio(): number | null {
        const c = this.actual?.campaignDto;
        if (!c?.objectiveBudgetMax) {
            return this.actual?.budgetConsumedRatio ?? null;
        }
        const objective = Number(c.objectiveBudgetMax);
        if (objective === 0) {
            return null;
        }
        const incremental = Number(this.actual?.incrementalBudget ?? 0);
        const initial = Number(c.costInitial ?? 0);
        const mode = c.campaignMode;
        const isExport = CampaignEditComponent.isExportFlux(c.flux);
        let numerator: number;
        let denominator: number;
        if (mode === 'SPREAD_EXPENSE' && isExport) {
            numerator = incremental;
            denominator = objective - initial;
            if (denominator <= 0) {
                return null;
            }
        } else {
            const consumed = this.getBudgetConsumed();
            if (consumed == null) {
                return null;
            }
            numerator = consumed;
            denominator = objective;
        }
        return Math.round((numerator / denominator) * 10000) / 100;
    }

    isSpreadCollapsedSynthesis(): boolean {
        if (this.actual?.campaignDto?.campaignMode !== 'SPREAD_EXPENSE') {
            return false;
        }
        const dto: any = this.actual;
        const orderPart = Number(dto?.incrementalBudgetOrder ?? 0);
        const contactPart = Number(dto?.incrementalBudgetContact ?? 0);
        const total = this.getBudgetConsumed();
        if (total == null) {
            return false;
        }
        if (orderPart > 0 && contactPart > 0) {
            return false;
        }
        const singlePart = orderPart > 0 ? orderPart : contactPart;
        return singlePart > 0 && Math.abs(singlePart - total) < 0.005;
    }

    showObservedContactPart(): boolean {
        const dto: any = this.actual;
        if (dto?.incrementalBudgetContact == null) {
            return false;
        }
        return !this.isSpreadCollapsedSynthesis();
    }

    showObservedOrderPart(): boolean {
        const dto: any = this.actual;
        if (this.actual?.campaignDto?.dataType !== CampaignDto.DataTypeEnum.ORDERS || dto?.incrementalBudgetOrder == null) {
            return false;
        }
        return !this.isSpreadCollapsedSynthesis();
    }

    showObservedTotalSeparator(): boolean {
        return this.showObservedContactPart() || this.showObservedOrderPart();
    }

    getObservedContactUnitLabel(): string {
        if (this.isSpreadExpenseCostPreview()) {
            return this.fin('field', 'unitContactEstimated');
        }
        return this.fin('field', 'unitContactObserved');
    }

    getObservedOrderUnitLabel(): string {
        if (this.isSpreadExpenseCostPreview()) {
            return this.fin('field', 'unitOrderEstimated');
        }
        return this.fin('field', 'unitOrderObserved');
    }

    getRatioPerObjectiveLabel(): string {
        const mode = this.actual?.campaignDto?.campaignMode;
        const isExport = CampaignEditComponent.isExportFlux(this.actual?.campaignDto?.flux);
        if (mode === 'SPREAD_EXPENSE' && isExport) {
            return this.translate.instant('campaign.edit.field.ratioSpreadRevenue');
        }
        return this.fin('field', 'ratioPerObjective');
    }

    getEstimatedBudgetHint(): string {
        const c = this.actual?.campaignDto;
        if (c?.dataType === CampaignDto.DataTypeEnum.ORDERS) {
            return this.fin('hint', 'estimatedBudgetOrders');
        }
        return this.fin('hint', 'estimatedBudget');
    }

    getCampaignModeUiLabel(): string {
        const mode = this.actual?.campaignDto?.campaignMode;
        const dataType = this.actual?.campaignDto?.dataType;
        if (mode === 'BUDGET_MAX') {
            return this.translate.instant('campaign.campaignMode.BUDGET_MAX');
        }
        if (mode === 'UNITS_MAX') {
            const key = dataType === CampaignDto.DataTypeEnum.ORDERS ? 'UNITS_MAX_ORDERS' : 'UNITS_MAX_CONTACTS';
            return this.translate.instant(`campaign.campaignMode.${key}`);
        }
        if (mode === 'SPREAD_EXPENSE') {
            return this.translate.instant('campaign.campaignMode.SPREAD_EXPENSE');
        }
        return mode || '-';
    }

    getCampaignModeIconClass(): string {
        const mode = this.actual?.campaignDto?.campaignMode;
        if (mode === 'BUDGET_MAX') return 'pi pi-wallet';
        if (mode === 'UNITS_MAX') return 'pi pi-list';
        return 'pi pi-clock';
    }

    getObservedTotalFormulaLabel(): string {
        const dto: any = this.actual;
        if (!this.actual || this.actual.incrementalBudget == null) {
            return '';
        }
        const mode = this.actual.campaignDto?.campaignMode;
        const isExport = CampaignEditComponent.isExportFlux(this.actual.campaignDto?.flux);
        if (mode === 'SPREAD_EXPENSE') {
            if (isExport && this.isSpreadCollapsedSynthesis()) {
                return '';
            }
            return this.trackingFormula(isExport ? 'formulaSpread' : 'formulaSpreadInitial');
        }
        const isOrdersCampaign = this.actual.campaignDto?.dataType === CampaignDto.DataTypeEnum.ORDERS;
        const isContactsCampaign = this.actual.campaignDto?.dataType === CampaignDto.DataTypeEnum.CONTACTS;
        const hasOrderPart = isOrdersCampaign;
        const hasContactPart = isContactsCampaign || dto?.incrementalBudgetContact != null;
        if (hasOrderPart && hasContactPart) {
            return this.trackingFormula('formulaBoth');
        }
        if (hasOrderPart) {
            return this.trackingFormula('formulaOrders');
        }
        if (hasContactPart) {
            return this.trackingFormula('formulaContacts');
        }
        return this.trackingFormula('formulaIncremental');
    }

    private trackingFormula(key: string): string {
        const suffix = CampaignEditComponent.isExportFlux(this.actual?.campaignDto?.flux) ? 'Revenue' : 'Cost';
        return this.translate.instant(`campaign.edit.tracking.${key}${suffix}`);
    }

    private isSpreadExpenseCostPreview(): boolean {
        return this.actual?.campaignDto?.campaignMode === 'SPREAD_EXPENSE'
            && this.actual?.campaignDto?.status !== CampaignDto.StatusEnum.TERMINATED;
    }

    getObservedOrderUnitCost(): number | null {
        const dto: any = this.actual;
        if (!this.actual || this.actual.campaignDto?.dataType !== CampaignDto.DataTypeEnum.ORDERS) {
            return null;
        }
        if (this.isSpreadExpenseCostPreview()) {
            return this.actual.costPerUnit != null ? Number(this.actual.costPerUnit) : null;
        }
        const appliedOrders = Number(this.actual.appliedUnits ?? 0);
        const incrementalOrder = Number(dto?.incrementalBudgetOrder ?? 0);
        if (appliedOrders > 0 && incrementalOrder > 0) {
            return incrementalOrder / appliedOrders;
        }
        if (this.actual.campaignDto?.campaignMode === 'SPREAD_EXPENSE' && this.actual.costPerUnit != null) {
            return Number(this.actual.costPerUnit);
        }
        return null;
    }

    getObservedContactUnitCost(): number | null {
        const dto: any = this.actual;
        if (!this.actual) {
            return null;
        }
        if (this.isSpreadExpenseCostPreview()
            && this.actual.campaignDto?.dataType === CampaignDto.DataTypeEnum.CONTACTS) {
            return this.actual.costPerUnit != null ? Number(this.actual.costPerUnit) : null;
        }
        const dataType = this.actual.campaignDto?.dataType;
        const contactBase = dataType === CampaignDto.DataTypeEnum.ORDERS
            ? Number(dto?.newContactsApplied ?? 0)
            : Number(this.actual.appliedUnits ?? 0);
        const incrementalContact = Number(dto?.incrementalBudgetContact ?? 0);
        if (contactBase > 0 && incrementalContact > 0) {
            return incrementalContact / contactBase;
        }
        if (this.actual.campaignDto?.campaignMode === 'SPREAD_EXPENSE' && this.actual.costPerUnit != null) {
            return Number(this.actual.costPerUnit);
        }
        return null;
    }

    openLinkedContacts(): void {
        const tenant = this.form?.get('tenant')?.value || this.tenant;
        const partner = this.form?.get('partner')?.value;
        const code = this.form?.get('code')?.value || this.actual?.campaignDto?.code;
        const flux = this.actual?.campaignDto?.flux;
        if (!tenant || !partner) {
            return;
        }
        const url =
            `/h/view/personal?tenant=${encodeURIComponent(tenant)}` +
            `&partner=${encodeURIComponent(partner)}` +
            `&code=personal&detail=false` +
            (code ? `&campaignCode=${encodeURIComponent(code)}` : '') +
            (flux ? `&campaignFlux=${encodeURIComponent(flux)}` : '');
        window.open(url, '_blank');
    }

    openLinkedCommands(): void {
        const tenant = this.form?.get('tenant')?.value || this.tenant;
        const partner = this.form?.get('partner')?.value;
        const code = this.form?.get('code')?.value || this.actual?.campaignDto?.code;
        const flux = this.actual?.campaignDto?.flux;
        if (!tenant || !partner) {
            return;
        }
        const url =
            `/h/view/command?tenant=${encodeURIComponent(tenant)}` +
            `&partner=${encodeURIComponent(partner)}` +
            `&code=command&detail=false` +
            (code ? `&campaignCode=${encodeURIComponent(code)}` : '') +
            (flux ? `&campaignFlux=${encodeURIComponent(flux)}` : '');
        window.open(url, '_blank');
    }

    initOnUpdate() {
        const id = this.route.snapshot.paramMap.get('id');
        if (id && this.tenant) {
            const isEditMode = this.router.url.includes('edit');

            this.engineCampaignService.get(this.tenant, parseInt(id)).subscribe((res) => {
                this.actual = res;

                // Block access to deleted campaigns
                if (this.actual.campaignDto.status === CampaignDto.StatusEnum.DELETED) {
                    this.messageService.add({
                        severity: 'warn',
                        summary: this.translate.instant('campaign.edit.message.accessDeniedSummary'),
                        detail: this.translate.instant('campaign.edit.message.deletedDetail')
                    });
                    this.router.navigate(['/h/marketing/campaign']);
                    return;
                }

                this.actual.campaignDto.dateEnd = this.actual.campaignDto.dateEnd ? moment(this.actual.campaignDto.dateEnd).toDate() : null;
                this.actual.campaignDto.dateStart = this.actual.campaignDto.dateStart ? moment(this.actual.campaignDto.dateStart).toDate() : null;

                // Validation: Une campagne peut être modifiée uniquement si son état est Brouillon ou Suspendue
                if (isEditMode) {
                    const status = this.actual.campaignDto.status;
                    if (status !== CampaignDto.StatusEnum.DRAFT && status !== CampaignDto.StatusEnum.SUSPENDED) {
                        this.messageService.add({
                            key: 'component',
                            severity: 'error',
                            summary: this.translate.instant('campaign.edit.message.editForbiddenSummary'),
                            detail: this.translate.instant('campaign.edit.message.editForbiddenDetail')
                        });
                        // Rediriger vers la vue détails (lecture seule)
                        this.router.navigate(['/h/marketing/campaign/', this.tenant, id]);
                        return;
                    }
                }

                const dto = this.actual.campaignDto as any;
                const isSpread = dto.campaignMode === 'SPREAD_EXPENSE';
                this.form.patchValue({
                    ...this.actual.campaignDto,
                    costUnitContact: isSpread ? null : (dto.costUnitContact ?? 0),
                    costUnitOrder: isSpread ? null : (dto.costUnitOrder ?? 0),
                    alertThresholdUnits: CampaignEditComponent.coerceOptionalPercentForForm(dto.alertThresholdUnits),
                    alertThresholdCost: CampaignEditComponent.coerceOptionalPercentForForm(dto.alertThresholdCost)
                }, { emitEvent: false });
                // valueChanges sur dataType n'est pas émis (emitEvent: false) : aligner les libellés objectif (commandes / contacts).
                this.updateListCampaignMode();
                this.updateScenarioExportValidators(this.form.get('flux')?.value);
                this.updateProviderValidator(this.form.get('flux')?.value);
                this.updateCampaignModeValidators();
                this.resetHiddenCampaignModeFields();
                this.rebuildAlertEmailTokensFromForm();

                // Lorsque la campagne n'est pas en mode Brouillon, les champs suivants deviennent en lecture seule
                if (this.actual.campaignDto.status !== CampaignDto.StatusEnum.DRAFT && !this.disabled) {
                    this.applyNonDraftFieldRestrictions();
                }

                // Refresh dropdowns with current form values (partner, product, dataType, flux) so selected options display
                this.loadScenarios();
                this.loadImports();
                this.loadExports();

                this.campaignItem = null;
                this.updateItem();
            });
        }
    }

    get f(): { [key: string]: AbstractControl } {
        return this.form.controls;
    }

    isNonDraftImmutableLock(): boolean {
        return this.actual?.campaignDto?.status != null
            && this.actual.campaignDto.status !== CampaignDto.StatusEnum.DRAFT;
    }

    readonly userTimezone: string = (() => {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const offsetMinutes = -new Date().getTimezoneOffset();
        const sign = offsetMinutes >= 0 ? '+' : '-';
        const hours = Math.floor(Math.abs(offsetMinutes) / 60);
        const minutes = Math.abs(offsetMinutes) % 60;
        const utcOffset = `UTC${sign}${hours}${minutes ? ':' + String(minutes).padStart(2, '0') : ''}`;
        return `${tz} (${utcOffset})`;
    })();

    // Defensive sync: ensures the reactive form control is always up to date
    // when p-calendar emits onSelect (fires for date clicks AND time spinner changes).
    onCalendarSelect(field: 'dateStart' | 'dateEnd', value: Date): void {
        if (value instanceof Date && !isNaN(value.getTime())) {
            this.form.get(field)?.setValue(value, { emitEvent: false });
        }
    }

    // Validation helpers for accordion sections
    hasErrorsInSection1(): boolean {
        if (!this.submitted) return false;
        const fields = ['tenant', 'partner', 'code', 'name', 'description', /* transferMode */ 'dataType', 'type', 'support', 'schedule'];
        return fields.some(field => this.form.get(field)?.invalid);
    }

    hasErrorsInSection2(): boolean {
        if (!this.submitted) return false;
        const fields = ['product', 'flux', 'source', 'destination', 'scenario', 'provider', 'importEtl', 'exportEtl'];
        return fields.some(field => this.form.get(field)?.invalid);
    }

    hasErrorsInSection3(): boolean {
        if (!this.submitted) return false;
        const fields = ['dateStart', 'dateEnd', 'costInitial', 'costUnitContact', 'costUnitOrder', 'campaignMode', 'objectiveUnits', 'objectiveBudgetMax'];
        const hasFieldErrors = fields.some(field => this.form.get(field)?.invalid);
        const hasCampaignModeErrors = this.form.hasError('campaignModeBudgetMax') || this.form.hasError('campaignModeUnitsMax') || this.form.hasError('campaignModeSpreadExpense');
        return hasFieldErrors || hasCampaignModeErrors;
    }

    hasErrorsInSection4(): boolean {
        if (!this.submitted) return false;
        const mode = this.form.get('campaignMode')?.value;
        if (mode === 'UNITS_MAX' && this.form.get('alertThresholdUnits')?.invalid) {
            return true;
        }
        if (mode === 'BUDGET_MAX' && this.form.get('alertThresholdCost')?.invalid) {
            return true;
        }
        return this.hasInvalidAlertEmailsForAlertsModes();
    }

    hasInvalidAlertEmailsForAlertsModes(): boolean {
        return this.alertEmailTokens.some(t => !t.valid);
    }

    private static isValidAlertEmail(email: string): boolean {
        return /^[\w.+-]+@[\w.-]+\.[a-zA-Z]{2,}$/.test((email || '').trim());
    }

    private static parseAlertEmailsToTokens(raw: string | null | undefined): AlertEmailToken[] {
        if (raw == null || String(raw).trim() === '') {
            return [];
        }
        const tokens: AlertEmailToken[] = [];
        for (const part of String(raw).split(/[\s,;]+/)) {
            const t = part.trim();
            if (!t) {
                continue;
            }
            tokens.push({ text: t, valid: CampaignEditComponent.isValidAlertEmail(t) });
        }
        return tokens;
    }

    private rebuildAlertEmailTokensFromForm(): void {
        const v = this.form?.get('alertEmailRecipients')?.value;
        this.alertEmailTokens = CampaignEditComponent.parseAlertEmailsToTokens(v);
    }

    private syncAlertEmailFormControlFromTokens(): void {
        const s = this.alertEmailTokens.length === 0
            ? ''
            : this.alertEmailTokens.map(t => t.text).join(',');
        this.form?.get('alertEmailRecipients')?.setValue(s, { emitEvent: false });
    }

    private pushAlertEmailToken(email: string): void {
        const t = (email || '').trim();
        if (!t) {
            return;
        }
        if (this.alertEmailTokens.some(x => x.text.toLowerCase() === t.toLowerCase())) {
            return;
        }
        this.alertEmailTokens = [...this.alertEmailTokens, { text: t, valid: CampaignEditComponent.isValidAlertEmail(t) }];
        this.syncAlertEmailFormControlFromTokens();
    }

    removeAlertEmailToken(index: number): void {
        this.alertEmailTokens = this.alertEmailTokens.filter((_, i) => i !== index);
        this.syncAlertEmailFormControlFromTokens();
    }

    onAlertEmailDraftKeydown(event: KeyboardEvent): void {
        if (event.key !== 'Enter') {
            return;
        }
        event.preventDefault();
        this.flushAlertEmailDraftToTokens();
    }

    flushAlertEmailDraftToTokens(): void {
        const raw = (this.alertEmailDraft || '').trim();
        if (!raw) {
            return;
        }
        for (const part of raw.split(/[\s,;]+/)) {
            const p = part.trim();
            if (p) {
                this.pushAlertEmailToken(p);
            }
        }
        this.alertEmailDraft = '';
    }

    addMyEmailAsAlertRecipient(): void {
        this.accountService.identity().then(user => {
            const email = user?.email?.trim();
            if (!email) {
                this.messageService.add({
                    key: 'component',
                    severity: 'warn',
                    summary: this.translate.instant('campaign.edit.message.profileSummary'),
                    detail: this.translate.instant('campaign.edit.message.noEmailDetail')
                });
                return;
            }
            this.pushAlertEmailToken(email);
        });
    }

    private static normalizeAlertEmailRecipientsForApi(tokens: AlertEmailToken[]): string | null {
        const valids = tokens.filter(t => t.valid).map(t => t.text.trim());
        if (valids.length === 0) {
            return null;
        }
        const seen = new Set<string>();
        const ordered: string[] = [];
        for (const e of valids) {
            const k = e.toLowerCase();
            if (!seen.has(k)) {
                seen.add(k);
                ordered.push(e);
            }
        }
        return ordered.join(',');
    }

    /**
     * Optional % for API (null = not set). Avoids treating empty p-inputNumber / strings as 0.
     */
    private static normalizeOptionalPercent(value: unknown): number | null {
        if (value === null || value === undefined) {
            return null;
        }
        if (typeof value === 'string') {
            const t = value.trim().replace(',', '.');
            if (t === '') {
                return null;
            }
            const n = Number(t);
            return Number.isNaN(n) ? null : n;
        }
        if (typeof value === 'number') {
            return Number.isNaN(value) ? null : value;
        }
        const n = Number(value);
        return Number.isNaN(n) ? null : n;
    }

    /** Coerce API / JSON values so p-inputNumber binds correct numbers when loading the form. */
    private static coerceOptionalPercentForForm(value: unknown): number | null {
        return CampaignEditComponent.normalizeOptionalPercent(value);
    }

    /** Objectif unités : valeur renseignée et ≥ 1 (0 exclu). */
    private static hasPositiveObjectiveUnits(value: unknown): boolean {
        if (value === null || value === undefined || value === '') {
            return false;
        }
        const n = typeof value === 'number' ? value : Number(value);
        return !Number.isNaN(n) && n >= 1;
    }

    /** Budget objectif : renseigné ; 0 € autorisé ; null / vide interdit quand requis côté formulaire. */
    private static hasBudgetObjectiveValue(value: unknown): boolean {
        if (value === null || value === undefined || value === '') {
            return false;
        }
        const n = typeof value === 'number' ? value : Number(value);
        return !Number.isNaN(n) && n >= 0;
    }

    hasErrorsInSection5(): boolean {
        if (!this.submitted) return false;
        const fields = ['utmSource', 'utmMedium', 'utmCampaign', 'utmTerm', 'utmContent'];
        const hasFieldErrors = fields.some(field => this.form.get(field)?.invalid);
        const hasAtLeastOneUtmError = this.form.get('flux')?.value === CampaignDto.FluxEnum.IMPORT && this.form.hasError('atLeastOneUtm');
        return hasFieldErrors || hasAtLeastOneUtmError;
    }

    /**
     * Validator: for IMPORT campaigns, at least one of the five UTM fields must be non-empty.
     */
    private atLeastOneUtmValidator(control: AbstractControl): ValidationErrors | null {
        const group = control as UntypedFormGroup;
        const flux = group.get('flux')?.value;
        if (flux !== CampaignDto.FluxEnum.IMPORT) {
            return null;
        }
        const utmFields = ['utmSource', 'utmMedium', 'utmCampaign', 'utmTerm', 'utmContent'];
        const hasOne = utmFields.some(field => {
            const value = group.get(field)?.value;
            return value != null && value.toString().trim().length > 0;
        });
        return hasOne ? null : { atLeastOneUtm: true };
    }

    /**
     * Validator: required fields depend on campaign mode.
     */
    private campaignModeValidator(control: AbstractControl): ValidationErrors | null {
        const group = control as UntypedFormGroup;
        const mode = group.get('campaignMode')?.value;
        const dataType = group.get('dataType')?.value;
        if (!mode) return null;
        if (mode === 'BUDGET_MAX') {
            const obj = group.get('objectiveBudgetMax')?.value;
            const costUnitContact = group.get('costUnitContact')?.value;
            const costUnitOrder = group.get('costUnitOrder')?.value;
            const hasObj = CampaignEditComponent.hasBudgetObjectiveValue(obj);
            const hasCostUnitContact = costUnitContact != null && costUnitContact !== '';
            const hasCostUnitOrder = dataType !== 'ORDERS' || (costUnitOrder != null && costUnitOrder !== '');
            return (hasObj && hasCostUnitContact && hasCostUnitOrder) ? null : { campaignModeBudgetMax: true };
        }
        if (mode === 'UNITS_MAX') {
            const obj = group.get('objectiveUnits')?.value;
            const costUnitContact = group.get('costUnitContact')?.value;
            const costUnitOrder = group.get('costUnitOrder')?.value;
            const hasObj = CampaignEditComponent.hasPositiveObjectiveUnits(obj);
            const hasCostUnitContact = costUnitContact != null && costUnitContact !== '';
            const hasCostUnitOrder = dataType !== 'ORDERS' || (costUnitOrder != null && costUnitOrder !== '');
            return (hasObj && hasCostUnitContact && hasCostUnitOrder) ? null : { campaignModeUnitsMax: true };
        }
        if (mode === 'SPREAD_EXPENSE') {
            const dateEnd = group.get('dateEnd')?.value;
            const budget = group.get('objectiveBudgetMax')?.value;
            const hasDateEnd = dateEnd && (typeof dateEnd !== 'object' || (dateEnd instanceof Date && !isNaN(dateEnd.getTime())));
            const hasBudget = CampaignEditComponent.hasBudgetObjectiveValue(budget);
            return (hasDateEnd && hasBudget) ? null : { campaignModeSpreadExpense: true };
        }
        return null;
    }

    private updateListCampaignMode(): void {
        const dataType = this.form?.get('dataType')?.value;
        const unitsKey = dataType === CampaignDto.DataTypeEnum.ORDERS ? 'UNITS_MAX_ORDERS' : 'UNITS_MAX_CONTACTS';
        this.listCampaignMode = [
            {label: this.translate.instant('campaign.campaignMode.BUDGET_MAX'), value: 'BUDGET_MAX'},
            {label: this.translate.instant(`campaign.campaignMode.${unitsKey}`), value: 'UNITS_MAX'},
            {label: this.translate.instant('campaign.campaignMode.SPREAD_EXPENSE'), value: 'SPREAD_EXPENSE'}
        ];
    }

    private updateCampaignModeValidators(): void {
        const mode = this.form?.get('campaignMode')?.value;
        const dataType = this.form?.get('dataType')?.value;
        const costUnitContact = this.form?.get('costUnitContact');
        const costUnitOrder = this.form?.get('costUnitOrder');
        const objectiveBudgetMax = this.form?.get('objectiveBudgetMax');
        const objectiveUnits = this.form?.get('objectiveUnits');
        const dateEnd = this.form?.get('dateEnd');
        costUnitContact?.clearValidators();
        costUnitOrder?.clearValidators();
        objectiveBudgetMax?.clearValidators();
        objectiveUnits?.clearValidators();
        dateEnd?.clearValidators();
        const alertUnits = this.form?.get('alertThresholdUnits');
        const alertCost = this.form?.get('alertThresholdCost');
        alertUnits?.clearValidators();
        alertCost?.clearValidators();
        if (mode === 'BUDGET_MAX') {
            costUnitContact?.setValidators([Validators.required, Validators.min(0)]);
            objectiveBudgetMax?.setValidators([Validators.required, Validators.min(0)]);
            if (dataType === 'ORDERS') costUnitOrder?.setValidators([Validators.required, Validators.min(0)]);
            else costUnitOrder?.setValidators([Validators.min(0)]);
            alertCost?.setValidators([Validators.min(0), Validators.max(100)]);
        } else if (mode === 'UNITS_MAX') {
            costUnitContact?.setValidators([Validators.required, Validators.min(0)]);
            objectiveUnits?.setValidators([Validators.required, Validators.min(1)]);
            if (dataType === 'ORDERS') costUnitOrder?.setValidators([Validators.required, Validators.min(0)]);
            else costUnitOrder?.setValidators([Validators.min(0)]);
            alertUnits?.setValidators([Validators.min(0), Validators.max(100)]);
        } else if (mode === 'SPREAD_EXPENSE') {
            dateEnd?.setValidators([Validators.required]);
            objectiveBudgetMax?.setValidators([Validators.required, Validators.min(0)]);
        }
        costUnitContact?.updateValueAndValidity();
        costUnitOrder?.updateValueAndValidity();
        objectiveBudgetMax?.updateValueAndValidity();
        objectiveUnits?.updateValueAndValidity();
        dateEnd?.updateValueAndValidity();
        alertUnits?.updateValueAndValidity({ emitEvent: false });
        alertCost?.updateValueAndValidity({ emitEvent: false });
    }

    /**
     * Reset fields that are hidden for the current campaign mode to their default values.
     * Ensures we don't persist stale values from a previously selected mode.
     */
    private resetHiddenCampaignModeFields(): void {
        const mode = this.form?.get('campaignMode')?.value;
        if (!mode) return;

        if (mode === 'BUDGET_MAX') {
            this.form?.get('objectiveUnits')?.setValue(null, { emitEvent: false });
        } else if (mode === 'UNITS_MAX') {
            this.form?.get('objectiveBudgetMax')?.setValue(null, { emitEvent: false });
        } else if (mode === 'SPREAD_EXPENSE') {
            this.form?.get('objectiveUnits')?.setValue(null, { emitEvent: false });
            this.form?.get('costUnitContact')?.setValue(null, { emitEvent: false });
            this.form?.get('costUnitOrder')?.setValue(null, { emitEvent: false });
        }
        if (mode === 'BUDGET_MAX') {
            this.form?.get('alertThresholdUnits')?.setValue(null, { emitEvent: false });
        } else if (mode === 'UNITS_MAX') {
            this.form?.get('alertThresholdCost')?.setValue(null, { emitEvent: false });
        } else if (mode === 'SPREAD_EXPENSE') {
            this.form?.get('alertThresholdUnits')?.setValue(null, { emitEvent: false });
            this.form?.get('alertThresholdCost')?.setValue(null, { emitEvent: false });
        }
    }

    isUtmMissingForImport(): boolean {
        if (this.form?.get('flux')?.value !== CampaignDto.FluxEnum.IMPORT) {
            return false;
        }
        const utmFields = ['utmSource', 'utmMedium', 'utmCampaign', 'utmTerm', 'utmContent'];
        return utmFields.every(field => {
            const value = this.form.get(field)?.value;
            return !value || value.toString().trim().length === 0;
        });
    }

    submit() {
        this.submitted = true;

        // Validation du formulaire
        if (this.form.invalid) {
            this.expandAllAccordion();
            this.messageService.add({
                key: 'component',
                severity: 'error',
                summary: this.translate.instant('campaign.edit.message.validationSummary'),
                detail: this.translate.instant('campaign.edit.validation.formErrors')
            });
            return;
        }

        this.flushAlertEmailDraftToTokens();
        if (this.hasInvalidAlertEmailsForAlertsModes()) {
            this.expandAllAccordion();
            this.messageService.add({
                key: 'component',
                severity: 'error',
                summary: this.translate.instant('campaign.edit.message.validationSummary'),
                detail: this.translate.instant('campaign.edit.validation.invalidAlertChips')
            });
            return;
        }

        const value = this.form.getRawValue(); // getRawValue() pour inclure les champs disabled
        let campaign: Partial<CampaignDto> = {};
        if (this.actual !== null && this.actual.campaignDto !== null) {
            campaign = {...this.actual.campaignDto}; // Clone pour éviter la mutation
        }

        // Mise à jour des valeurs
        campaign.tenant = value.tenant;
        campaign.partner = value.partner;
        campaign.name = value.name;
        campaign.code = value.code;
        campaign.description = value.description;
        campaign.product = value.product;
        campaign.flux = value.flux;
        campaign.destination = value.destination;
        campaign.source = value.source;
        campaign.type = value.type;
        campaign.provider = value.provider;
        campaign.support = value.support;
        campaign.schedule = value.schedule;
        campaign.dateStart = value.dateStart && value.dateStart instanceof Date && !isNaN(value.dateStart.getTime()) ?
            value.dateStart : null;
        campaign.dateEnd = value.dateEnd && value.dateEnd instanceof Date && !isNaN(value.dateEnd.getTime()) ?
            value.dateEnd : null;
        campaign.costInitial = value.costInitial;
        campaign.disable = value.disable;
        // À supprimer: transferMode lors de la suppression totale
        // campaign.transferMode = value.transferMode;
        campaign.scenario = value.scenario;
        campaign.importEtl = value.importEtl || undefined;
        campaign.exportEtl = value.exportEtl || undefined;
        campaign.dataType = value.dataType;
        campaign.campaignMode = value.campaignMode;
        // Use defaults for fields hidden by current mode (don't persist stale values)
        const mode = value.campaignMode;
        const dataType = value.dataType;
        campaign.objectiveUnits = (mode === 'UNITS_MAX') ? value.objectiveUnits : null;
        campaign.objectiveBudgetMax = (mode === 'BUDGET_MAX' || mode === 'SPREAD_EXPENSE') ? value.objectiveBudgetMax : null;
        const note = typeof value.financialNote === 'string' ? value.financialNote.trim() : '';
        (campaign as CampaignDto).financialNote = note.length > 0 ? note : undefined;
        if (mode === 'SPREAD_EXPENSE') {
            (campaign as any).costUnitContact = null;
            (campaign as any).costUnitOrder = null;
        } else if (mode === 'BUDGET_MAX' || mode === 'UNITS_MAX') {
            campaign.costUnitContact = value.costUnitContact;
            campaign.costUnitOrder = (dataType === 'ORDERS') ? value.costUnitOrder : 0;
        } else {
            (campaign as any).costUnitContact = null;
            (campaign as any).costUnitOrder = null;
        }
        campaign.alertThresholdUnits = (mode === 'UNITS_MAX')
            ? CampaignEditComponent.normalizeOptionalPercent(value.alertThresholdUnits)
            : null;
        campaign.alertThresholdCost = (mode === 'BUDGET_MAX')
            ? CampaignEditComponent.normalizeOptionalPercent(value.alertThresholdCost)
            : null;
        const recipients = CampaignEditComponent.normalizeAlertEmailRecipientsForApi(this.alertEmailTokens);
        Object.assign(campaign, { alertEmailRecipients: recipients ?? undefined });
        campaign.utmSource = value.utmSource;
        campaign.utmMedium = value.utmMedium;
        campaign.utmCampaign = value.utmCampaign;
        campaign.utmTerm = value.utmTerm;
        campaign.utmContent = value.utmContent;
        campaign.status = value.status;

        // Validation métier - règles de la user story de création
        if (this.isUtmMissingForImport()) {
            this.messageService.add({
                key: 'component',
                severity: 'error',
                summary: this.translate.instant('campaign.edit.message.validationSummary'),
                detail: this.translate.instant('campaign.edit.validation.importUtm')
            });
            return;
        }

        if (campaign.flux === CampaignDto.FluxEnum.IMPORT && (!campaign.provider || campaign.provider.trim().length === 0)) {
            this.messageService.add({
                key: 'component',
                severity: 'error',
                summary: this.translate.instant('campaign.edit.message.validationSummary'),
                detail: this.translate.instant('campaign.edit.validation.importProvider')
            });
            return;
        }
        if (campaign.flux === CampaignDto.FluxEnum.EXPORTSCENARIO && (!campaign.scenario || campaign.scenario.trim().length === 0)) {
            this.messageService.add({
                key: 'component',
                severity: 'error',
                summary: this.translate.instant('campaign.edit.message.validationSummary'),
                detail: this.translate.instant('campaign.edit.validation.exportScenario')
            });
            return;
        }
        if (campaign.flux === CampaignDto.FluxEnum.EXPORTDIRECT && (!campaign.exportEtl || campaign.exportEtl.trim().length === 0)) {
            this.messageService.add({
                key: 'component',
                severity: 'error',
                summary: this.translate.instant('campaign.edit.message.validationSummary'),
                detail: this.translate.instant('campaign.edit.validation.exportDirect')
            });
            return;
        }

        const hasDateEnd = campaign.dateEnd != null
            && (!(campaign.dateEnd instanceof Date) || !isNaN(campaign.dateEnd.getTime()));
        const hasUnitsObjective = CampaignEditComponent.hasPositiveObjectiveUnits(campaign.objectiveUnits);
        const hasBudgetObjective = CampaignEditComponent.hasBudgetObjectiveValue(campaign.objectiveBudgetMax);
        if (!hasDateEnd && !hasUnitsObjective && !hasBudgetObjective) {
            this.messageService.add({
                key: 'component',
                severity: 'error',
                summary: this.translate.instant('campaign.edit.message.validationSummary'),
                detail: this.translate.instant('campaign.edit.validation.endOrObjective')
            });
            return;
        }

        const isNew = campaign.id ? false : true;

        const confirmMessage = isNew
            ? this.translate.instant('campaign.edit.confirm.saveCreate', {name: campaign.name})
            : this.translate.instant('campaign.edit.confirm.saveUpdate', {name: campaign.name});

        this.confirmationService.confirm({
            message: confirmMessage,
            header: this.translate.instant('campaign.edit.confirm.header'),
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: this.translate.instant('campaign.edit.confirm.accept'),
            rejectLabel: this.translate.instant('campaign.edit.confirm.reject'),
            accept: () => {
                this.saveCampaign(campaign as CampaignDto, isNew);
            }
        });
    }

    private saveCampaign(campaign: CampaignDto, isNew: boolean) {
        // Log de l'opération pour la traçabilité (audit DMP)
        const operation = isNew ? 'CREATE' : 'UPDATE';
        console.log(`[DMP Audit] Campaign ${operation}:`, {
            operation,
            timestamp: new Date().toISOString(),
            campaignId: campaign.id,
            campaignCode: campaign.code,
            campaignName: campaign.name,
            status: campaign.status,
            tenant: this.tenant,
            partner: campaign.partner
        });


        this.engineCampaignService.createOrUpdate(campaign, this.tenant, isNew).subscribe(
            (result) => {
                // Log de succès
                console.log(`[DMP Audit] Campaign ${operation} SUCCESS:`, {
                    operation,
                    timestamp: new Date().toISOString(),
                    campaignId: result.id,
                    campaignCode: result.code,
                    campaignName: result.name
                });

                // Message de succès
                const successMessage = isNew
                    ? this.translate.instant('campaign.edit.message.created')
                    : this.translate.instant('campaign.edit.message.updated');

                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('campaign.edit.message.successSummary'),
                    detail: successMessage
                });

                // Navigation avec message d'état
                const navigationExtras: NavigationExtras = {
                    state: {message: isNew ? 'success_created' : 'success_updated'}
                };
                this.router.navigate([`/h/marketing/campaign`], navigationExtras);
            },
            (error) => {
                // Log de l'erreur pour la traçabilité
                console.error(`[DMP Audit] Campaign ${operation} FAILED:`, {
                    operation,
                    timestamp: new Date().toISOString(),
                    campaignId: campaign.id,
                    campaignCode: campaign.code,
                    error: error
                });

                // Message d'erreur détaillé
                const errorDetail = error?.error?.message || error?.message || error.toString();
                this.messageService.add({
                    key: 'component',
                    severity: 'error',
                    summary: this.translate.instant('campaign.edit.message.errorSummary'),
                    detail: this.translate.instant('campaign.edit.message.saveFailed', {detail: errorDetail})
                });
            }
        );
    }


    public updateItem(event?: LazyLoadEvent) {
        if (this.actual?.campaignDto?.id) {
            this.itemPaginator.first = event ? event.first / this.itemPaginator.rows : 0;
            this.itemPaginator.loading = true;
            this.engineCampaignService.getAssociations(this.tenant, this.actual.campaignDto.id,
                this.itemPaginator.first, this.itemPaginator.rows, null, 'response').subscribe(res => {
                this.campaignItems = res.body ?? [];
                this.itemPaginator.loading = false;
                this.itemPaginator.totalRecords = +res.headers.get('x-total-count');
                if (this.campaignItems.length === 0) {
                    this.campaignItem = null;
                } else if (this.disabled && !this.campaignItem) {
                    this.setCampaignItem(this.campaignItems[0]);
                }
            });
        }
    }

    public setCampaignItem(campaignItem: ICampaignAssociationItem) {
        this.campaignItem = campaignItem;
        this.updateAffectation();
    }

    getAssignmentsTitle(): string {
        if (!this.campaignItem?.date) {
            return '';
        }
        const formatted = moment(this.campaignItem.date).format('DD/MM/YYYY');
        return this.translate.instant('campaign.edit.field.assignmentsOf', {date: formatted});
    }

    hasCampaignHistoryItems(): boolean {
        if ((this.itemPaginator.totalRecords ?? 0) > 0) {
            return true;
        }
        return (this.campaignItems?.length ?? 0) > 0;
    }

    /**
     * Maps campaign product to expected scenario codeDataView (Données) values.
     * Scenario process.codeDataView must match one of these for the scenario to be shown.
     */
    private static readonly PRODUCT_TO_DATA_VIEW_CODES: Record<string, string[]> = {
        FLIGHT: ['command-flight'],
        HOTEL: ['command-hotel'],
        CAR: ['command-car', 'command-carrental'],
        PARKING: ['command-parking'],
        ALL: ['command']
    };

    /**
     * Matches codeDataView against expected codes.
     * - Commands: exact match only (command-flight, command-hotel, etc. - no variants).
     * - Contacts + ALL: exact match only (personal = tous les contacts).
     * - Contacts + product-specific: exact or personal-* (personal-flight, personal-hotel, etc.).
     */
    private static matchesCodeDataView(codeDataView: string, expectedCodes: string[], product: string, dataType: string): boolean {
        const codeLower = codeDataView.toLowerCase();
        const isCommands = dataType === CampaignDto.DataTypeEnum.ORDERS;
        const isAllProduct = product === 'ALL';
        if (isCommands || isAllProduct) {
            return expectedCodes.some(exp => codeLower === exp.toLowerCase());
        }
        return expectedCodes.some(exp => {
            const expLower = exp.toLowerCase();
            return codeLower === expLower || codeLower.startsWith(expLower + '-');
        });
    }

    private loadScenarios() {
        const partnerCode = this.form?.get('partner')?.value;
        const product = this.form?.get('product')?.value;
        const dataType = this.form?.get('dataType')?.value;
        if (!this.tenant || !partnerCode || !product || !dataType) {
            this.listScenario = [];
            return;
        }
        const applyFilter = (scenarios: ScenarioDTO[]) => {
            let filtered = scenarios.filter(s => s.affectedPartners && s.affectedPartners.includes(partnerCode));
            filtered = filtered.filter(s => s.statusScenario !== ScenarioDTO.StatusScenarioEnum.ARCHIVE);
            const expectedCodes = dataType === CampaignDto.DataTypeEnum.CONTACTS
                ? ['personal']
                : (CampaignEditComponent.PRODUCT_TO_DATA_VIEW_CODES[product] || []);
            filtered = filtered.filter(s => {
                const codeDataView = (s.process as MarketingScenarioProcess)?.codeDataView;
                if (!codeDataView) return true;
                return CampaignEditComponent.matchesCodeDataView(codeDataView, expectedCodes, product, dataType);
            });
            const marketing = filtered.filter(s => s.type === ScenarioDTO.TypeEnum.MARKETING).map(s => ({label: s.name, value: s.scenarioId}));
            const transactional = filtered.filter(s => s.type === ScenarioDTO.TypeEnum.TRANSACTIONAL).map(s => ({label: s.name, value: s.scenarioId}));
            const other = filtered.filter(s => s.type !== ScenarioDTO.TypeEnum.MARKETING && s.type !== ScenarioDTO.TypeEnum.TRANSACTIONAL).map(s => ({label: s.name, value: s.scenarioId}));
            this.listScenario = [];
            if (marketing.length > 0) {
                this.listScenario.push({ label: this.translate.instant('campaign.edit.scenario.marketing'), value: null, disabled: true });
                this.listScenario.push(...marketing);
            }
            if (transactional.length > 0) {
                this.listScenario.push({ label: this.translate.instant('campaign.edit.scenario.transactional'), value: null, disabled: true });
                this.listScenario.push(...transactional);
            }
            if (other.length > 0) {
                this.listScenario.push({ label: this.translate.instant('campaign.edit.scenario.other'), value: null, disabled: true });
                this.listScenario.push(...other);
            }
        };
        const cached = this.allScenariosByPartner.get(partnerCode);
        if (cached) {
            applyFilter(cached);
            return;
        }
        this.configuratorScenarioService.listOrLatest(this.tenant, partnerCode).subscribe((scenarios: ScenarioDTO[]) => {
            this.allScenariosByPartner.set(partnerCode, scenarios || []);
            applyFilter(scenarios || []);
        });
    }

    private loadImports() {
        const partnerCode = this.form?.get('partner')?.value;
        const flux = this.form?.get('flux')?.value;
        if (!this.tenant || flux !== CampaignDto.FluxEnum.IMPORT) {
            this.listImport = [];
            return;
        }
        if (!partnerCode) {
            this.listImport = [];
            return;
        }
        const applyFilter = (etls: EtlConf[]) => {
            const filtered = etls.filter(e => {
                const partners = e.personalDataAccessPartners || [];
                return partners.includes(partnerCode);
            });
            this.listImport = filtered.map(e => ({ label: e.name || e.code || e.id, value: e.id }));
        };
        if (this.allImportEtls !== null) {
            applyFilter(this.allImportEtls);
            return;
        }
        this.configuratorEtlService.getAllEtls(this.tenant, ['IMPORT']).subscribe((etls: EtlConf[]) => {
            this.allImportEtls = etls || [];
            applyFilter(this.allImportEtls);
        });
    }

    private loadExports() {
        const partnerCode = this.form?.get('partner')?.value;
        const productCode = this.form?.get('product')?.value;
        const dataType = this.form?.get('dataType')?.value;
        const flux = this.form?.get('flux')?.value;
        if (!this.tenant || flux !== CampaignDto.FluxEnum.EXPORTDIRECT) {
            this.listExport = [];
            return;
        }
        if (!partnerCode || !productCode || !dataType) {
            this.listExport = [];
            return;
        }
        const applyFilter = (etls: EtlConf[]) => {
            let filtered = etls.filter(e => {
                const partners = e.affectedPartners || e.personalDataAccessPartners || [];
                return partners.includes(partnerCode);
            });
            filtered = filtered.filter(e =>
                productCode === 'ALL'
                    ? (!e.type || e.type?.toLowerCase() === 'all')
                    : (!e.type || e.type?.toLowerCase() === productCode.toLowerCase()));
            const expectedCodes = dataType === CampaignDto.DataTypeEnum.CONTACTS
                ? ['personal']
                : (CampaignEditComponent.PRODUCT_TO_DATA_VIEW_CODES[productCode] || []);
            filtered = filtered.filter(e => {
                const view = (e.extract?.typeObject === 'SaveQueryExtractConf' && (e.extract as SaveQueryExtractConf).saveQuery)
                    ? (e.extract as SaveQueryExtractConf).saveQuery.view
                    : null;
                if (!view) return false;
                return CampaignEditComponent.matchesCodeDataView(view, expectedCodes, productCode, dataType);
            });
            this.listExport = filtered.map(e => ({ label: e.name || e.code || e.id, value: e.id }));
            const currentExportId = this.form?.get('exportEtl')?.value;
            if (currentExportId != null && String(currentExportId).trim() !== ''
                && !this.listExport.some(o => o.value === currentExportId)) {
                this.form.get('exportEtl')?.setValue('', { emitEvent: false });
            }
        };
        if (this.allExportEtls !== null) {
            applyFilter(this.allExportEtls);
            return;
        }
        this.configuratorEtlService.getAllEtls(this.tenant, ['EXPORT']).subscribe((etls: EtlConf[]) => {
            this.allExportEtls = etls || [];
            (this.allExportEtls || []).forEach(e => this.exportEtlNameById.set(e.id, e.name || e.code || e.id));
            applyFilter(this.allExportEtls);
        });
    }

    public updateAffectation(event?: LazyLoadEvent) {
        if (!this.campaignItem || !this.campaignItem.date || !this.campaignItem.origin) {
            this.campaignJoins = [];
            this.personalPaginator.loading = false;
            this.personalPaginator.totalRecords = 0;
            return;
        }
        
        this.personalPaginator.first = event ? event.first / this.personalPaginator.rows : 0;
        this.personalPaginator.loading = true;
        
        // Convert date to ISO string for API
        const dateISO = this.campaignItem.date instanceof Date 
            ? this.campaignItem.date.toISOString() 
            : new Date(this.campaignItem.date).toISOString();
        
        this.engineCampaignService.getPersonalForAssociations(
            this.tenant, 
            this.actual.campaignDto.id, 
            dateISO,
            this.campaignItem.origin,
            this.personalPaginator.first, 
            this.personalPaginator.rows, 
            null, 
            'response'
        ).subscribe(res => {
            this.campaignJoins = res.body;
            this.personalPaginator.loading = false;
            this.personalPaginator.totalRecords = +res.headers.get('x-total-count');
        });
    }

    deleteAffectationPersonal() {
        //this.engineCampaignService.
    }

    publishCampaign() {
        const updatedCampaign: CampaignDto = {
            ...this.actual.campaignDto,
            status: CampaignDto.StatusEnum.INPROGRESS
        };

        this.engineCampaignService.createOrUpdate(updatedCampaign, this.tenant, false).subscribe(
            () => {
                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('campaign.edit.message.successSummary'),
                    detail: this.translate.instant('campaign.edit.message.published')
                });
                // Redirect to listing after successful publication
                this.router.navigate(['/h/marketing/campaign']);
            },
            () => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('campaign.edit.message.errorSummary'),
                    detail: this.translate.instant('campaign.edit.message.publishFailed')
                });
            }
        );
    }

    suspendCampaign() {
        const updatedCampaign: CampaignDto = {
            ...this.actual.campaignDto,
            status: CampaignDto.StatusEnum.SUSPENDED
        };

        this.engineCampaignService.createOrUpdate(updatedCampaign, this.tenant, false).subscribe(
            () => {
                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('campaign.edit.message.successSummary'),
                    detail: this.translate.instant('campaign.edit.message.suspended')
                });
                // Redirect to listing after successful suspension
                this.router.navigate(['/h/marketing/campaign']);
            },
            () => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('campaign.edit.message.errorSummary'),
                    detail: this.translate.instant('campaign.edit.message.suspendFailed')
                });
            }
        );
    }

    terminateCampaign() {
        this.confirmationService.confirm({
            message: this.translate.instant('campaign.list.dialog.terminateMessage', {name: this.actual.campaignDto.name}),
            header: this.translate.instant('campaign.list.dialog.terminateTitle'),
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                const updatedCampaign: CampaignDto = {
                    ...this.actual.campaignDto,
                    status: CampaignDto.StatusEnum.TERMINATED
                };

                this.engineCampaignService.createOrUpdate(updatedCampaign, this.tenant, false).subscribe(
                    () => {
                        this.messageService.add({
                            severity: 'success',
                            summary: this.translate.instant('campaign.edit.message.successSummary'),
                            detail: this.translate.instant('campaign.edit.message.terminated')
                        });
                        // Redirect to listing after successful termination
                        this.router.navigate(['/h/marketing/campaign']);
                    },
                    () => {
                        this.messageService.add({
                            severity: 'error',
                            summary: this.translate.instant('campaign.edit.message.errorSummary'),
                            detail: this.translate.instant('campaign.edit.message.terminateFailed')
                        });
                    }
                );
            }
        });
    }

    deleteCampaign() {
        this.confirmationService.confirm({
            message: this.translate.instant('campaign.edit.confirm.deleteMessage', {name: this.actual.campaignDto.name}),
            header: this.translate.instant('campaign.edit.confirm.header'),
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                const updatedCampaign: CampaignDto = {
                    ...this.actual.campaignDto,
                    status: CampaignDto.StatusEnum.DELETED
                };

                this.engineCampaignService.createOrUpdate(updatedCampaign, this.tenant, false).subscribe(
                    () => {
                        this.messageService.add({
                            severity: 'success',
                            summary: this.translate.instant('campaign.edit.message.successSummary'),
                            detail: this.translate.instant('campaign.edit.message.deleted')
                        });
                        // Redirect to listing after successful deletion
                        this.router.navigate(['/h/marketing/campaign']);
                    },
                    () => {
                        this.messageService.add({
                            severity: 'error',
                            summary: this.translate.instant('campaign.edit.message.errorSummary'),
                            detail: this.translate.instant('campaign.edit.message.deleteFailed')
                        });
                    }
                );
            }
        });
    }
}
